
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, CalendarIcon, UserIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useDocsFeaturedImage } from '@/hooks/useWordPressDocs';
import { docsService } from '@/services/docsService';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import SEO from '@/components/SEO';

const SupportArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  
  const { data: post, isLoading: postLoading, error } = useQuery({
    queryKey: ['docs-post', slug],
    queryFn: () => docsService.fetchPost(slug || ''),
    enabled: !!slug,
  });
  const { data: author } = useQuery({
    queryKey: ['docs-author', post?.author],
    queryFn: () => docsService.fetchAuthor(post?.author || 0),
    enabled: !!(post?.author),
  });
  const { data: featuredImageUrl } = useDocsFeaturedImage(post);

  const estimateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = docsService.stripHtml(content).split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  if (postLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-4 bg-muted rounded w-32"></div>
              <div className="h-12 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="h-64 bg-muted rounded"></div>
              <div className="space-y-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="h-4 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The support article you're looking for doesn't exist or has been moved.
          </p>
          <Link to="/support" className="text-primary hover:underline">
            ← Back to Support Center
          </Link>
        </div>
      </div>
    );
  }

  const readingTime = estimateReadingTime(post.content.rendered);
  const publishDate = new Date(post.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      <SEO 
        title={`${docsService.stripHtml(post.title.rendered)} - Support - SeniorThrive`}
        description={docsService.stripHtml(post.excerpt.rendered).substring(0, 160)}
      />
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Navigation */}
            <Link 
              to="/support" 
              className="inline-flex items-center text-primary hover:underline mb-8"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Support Center
            </Link>

            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                {docsService.stripHtml(post.title.rendered)}
              </h1>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground mb-6">
                {author && (
                  <div className="flex items-center">
                    <UserIcon className="h-4 w-4 mr-1" />
                    {author.name}
                  </div>
                )}
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  {publishDate}
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  {readingTime}
                </div>
              </div>

              {/* Featured Image */}
              {featuredImageUrl && (
                <div className="mb-8">
                  <img 
                    src={featuredImageUrl} 
                    alt={docsService.stripHtml(post.title.rendered)}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}
            </header>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none">
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: sanitizeHtml(post.content.rendered, {
                    allowLinks: true,
                    allowBasicFormatting: true,
                    allowImages: true
                  })
                }}
                className="
                  [&>h1]:text-3xl [&>h1]:font-bold [&>h1]:mt-8 [&>h1]:mb-4
                  [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:mt-8 [&>h2]:mb-4 [&>h2]:text-primary
                  [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3
                  [&>h4]:text-lg [&>h4]:font-medium [&>h4]:mt-6 [&>h4]:mb-3
                  [&>p]:mb-4 [&>p]:leading-relaxed
                  [&>ul]:mb-4 [&>ul]:pl-6 [&>ul>li]:mb-2 [&>ul>li]:list-disc
                  [&>ol]:mb-4 [&>ol]:pl-6 [&>ol>li]:mb-2 [&>ol>li]:list-decimal
                  [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:my-6
                  [&>pre]:bg-muted [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-6
                  [&>code]:bg-muted [&>code]:px-2 [&>code]:py-1 [&>code]:rounded [&>code]:text-sm
                  [&>img]:rounded-lg [&>img]:my-6 [&>img]:w-full
                  [&>a]:text-primary [&>a]:underline [&>a]:hover:no-underline
                  [&>table]:w-full [&>table]:border-collapse [&>table]:my-6
                  [&>table_th]:border [&>table_th]:border-border [&>table_th]:p-2 [&>table_th]:bg-muted [&>table_th]:font-semibold
                  [&>table_td]:border [&>table_td]:border-border [&>table_td]:p-2
                "
              />
            </article>

            {/* Footer */}
            <footer className="mt-12 pt-8 border-t">
              <div className="bg-muted/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-2">Need More Help?</h3>
                <p className="text-muted-foreground mb-4">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center text-primary hover:underline font-medium"
                >
                  Contact Support →
                </Link>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportArticle;
